// ** React Imports
import { createContext, useEffect, useMemo, useRef, useState } from 'react'

// ** Redux Imports
import { useSelector, useDispatch } from 'react-redux'

// ** Actions
import { setMeliUsers, setLoading, setError, clearMeliData } from '@store/meli'

// ** useJwt
import useJwt from '@src/auth/jwt/useJwt'

// ** authMeli
// import authMeli from '@src/auth/meli/authMeli'

// ** Axios
import axios from 'axios'

// ** Create Context
export const MeliContext = createContext({
    meliUsers: [],
    defaultMeliUserId: null,
    isLoading: false,
    error: null,
    isInitialized: false,
    fetchMeliAuthUsers: async () => null,
    getMeliDefaultUser: () => null,
    getMeliAccessToken: () => null,
    getDefaultMeliAccessToken: () => null,
    isMeliTokenExpired: () => true,
    MelirefreshIfNeeded: async () => {},
    getMeliUserProfile: async () => null,
    fetchAndCacheAllMeliProfiles: async () => null
})

export const MeliContextProvider = ({ children }) => {
    const dispatch = useDispatch()
    const intervalRef = useRef(null)

    // ** Redux State
    const { meliUsers, defaultMeliUserId, isLoading, error } = useSelector(state => state.meli)
    const { userData } = useSelector(state => state.auth)

    // ** State
    const [isInitialized, setIsInitialized] = useState(false)

    const TEN_MINUTES = 10 * 60 * 1000

    // const fetchAndCacheMeliProfile = async (meliuserid, access_token) => {
    //     const localStorageKey = `meliuserid-${meliuserid}`
    //
    //     try {
    //         const response = await axios.get(`https://api.mercadolibre.com/users/me?access_token=${access_token}`, {
    //             headers: {
    //                 Authorization: `Bearer ${access_token}`
    //             }
    //         })
    //
    //         const dataToCache = {
    //             profile: response.data,
    //             cachedAt: Date.now()
    //         }
    //
    //         localStorage.setItem(localStorageKey, JSON.stringify(dataToCache))
    //         console.log(`✅ Perfil cacheado para ${meliuserid}`)
    //     } catch (err) {
    //         console.error(`❌ Error al obtener perfil para ${meliuserid}:`, err)
    //     }
    // }

    // const refreshCachedMeliProfiles = async () => {
    //     for (const user of meliUsers) {
    //         const { meliuserid, access_token } = user
    //         if (!meliuserid || !access_token) continue
    //         await fetchAndCacheMeliProfile(meliuserid, access_token)
    //     }
    // }

    const fetchAndCacheAllMeliProfiles = async (users) => {
        const now = Date.now()

        for (const user of users) {
             // console.log(user.meliuserid)
             // console.log(user.access_token)


            if (!user.meliuserid) continue

            const localStorageKey = `meliuserid-${user.meliuserid}`
            const cachedData = localStorage.getItem(localStorageKey)

            let shouldFetchImmediately = true

            if (cachedData) {
                // console.log('cached')
                try {
                    const parsed = JSON.parse(cachedData)
                    if (parsed.cachedAt && now - parsed.cachedAt < 10 * 60 * 1000) {
                        shouldFetchImmediately = false
                    }
                } catch (e) {
                    console.warn(`Error parsing cached profile for ${user.meliuserid}:`, e)
                    localStorage.removeItem(localStorageKey)
                }
            }

            if (shouldFetchImmediately) {
                // console.log('shouldFetchImmediately')
                try {
                        // const response = await authMeli.me()
                    // console.log(response)

                    // const response = await axios.get(`https://api.mercadolibre.com/users/me?access_token=${user.access_token}`, {
                    //     headers: {
                    //         Authorization: `Bearer ${user.access_token}`
                    //     }
                    // })

                    const response = await useJwt.get(`meli/user/me?meliuserid=${user.meliuserid}`)

                    const dataToCache = {
                        profile: response.data,
                        cachedAt: now
                    }

                    localStorage.setItem(localStorageKey, JSON.stringify(dataToCache))
                } catch (err) {
                    console.error(`Error fetching profile for user ${user.meliuserid}:`, err)
                }
            }
        }
    }

    const fetchMeliAuthUsers = async () => {
        try {
            dispatch(setLoading(true))

            const response = await useJwt.get('meli/authuser')

            if (response.data && Array.isArray(response.data)) {
                dispatch(setMeliUsers(response.data))
                // console.log(response.data)
                await fetchAndCacheAllMeliProfiles(response.data)
                return response.data
            } else {
                throw new Error('Formato de respuesta inválido')
            }
        } catch (err) {

            if (err.response?.status === 404) {
                // Caso esperado: el usuario no tiene configuraciones aún
                console.info('🟡 Usuario sin configuración de MercadoLibre aún.')
                dispatch(setMeliUsers([]))

            } else {
                console.error('Error fetching MercadoLibre users:', err)
                dispatch(setError(err.message || 'Error al obtener usuarios de MercadoLibre'))
            }

            return null
        } finally {
            dispatch(setLoading(false))
        }
    }

    const getMeliDefaultUser = () => {
        if (!defaultMeliUserId || meliUsers.length === 0) return null
        // console.log('user.meliuserid')
        // console.log(meliUsers)
        return meliUsers.find(user => user.meliuserid.toString() === defaultMeliUserId.toString())
    }

    const getMeliAccessToken = meliuserid => {
        const user = meliUsers.find(u => u.meliuserid.toString() === meliuserid.toString())
        return user ? user.access_token : null
    }

    const getDefaultMeliAccessToken = () => {
        const defaultUser = getMeliDefaultUser()
        return defaultUser ? defaultUser.access_token : null
    }

    const isMeliTokenExpired = meliuserid => {
        const user = meliUsers.find(u => u.meliuserid.toString() === meliuserid.toString())
        if (!user) return true
        const now = Math.floor(Date.now() / 1000)
        return user.expire < now
    }

    const MelirefreshIfNeeded = async () => {
        const defaultUser = getMeliDefaultUser()

        if (defaultUser && isMeliTokenExpired(defaultUser.meliuserid)) {
            console.log('Token expirado, refrescando...')
            await fetchMeliAuthUsers()
        }
    }

    const getMeliUserProfile = async meliuserid => {
        if (!meliuserid) return null

        const localStorageKey = `meliuserid-${meliuserid}`

        const cached = localStorage.getItem(localStorageKey)
        if (cached) {
            try {
                return JSON.parse(cached)
            } catch (e) {
                console.warn('Error parsing cached Meli user profile:', e)
                localStorage.removeItem(localStorageKey)
            }
        }

        const token = getMeliAccessToken(meliuserid)
        if (!token) {
            console.warn(`No access token found for meliuserid: ${meliuserid}`)
            return null
        }

        try {
            const response = await axios.get('https://api.mercadolibre.com/users/me2', {
                headers: {
                    Authorization: `Bearer ${token}`
                }
            })

            const userProfile = response.data
            localStorage.setItem(localStorageKey, JSON.stringify(userProfile))

            return userProfile
        } catch (err) {
            console.error('Error fetching Meli user profile:', err)
            return null
        }
    }

    const getCachedMeliUserProfile = (meliuserid) => {
        if (!meliuserid) return null

        const localStorageKey = `meliuserid-${meliuserid}`
        const cachedData = localStorage.getItem(localStorageKey)

        if (!cachedData) return null

        try {
            return JSON.parse(cachedData)
        } catch (e) {
            console.warn(`Error parsing cached profile for ${meliuserid}:`, e)
            localStorage.removeItem(localStorageKey)
            return null
        }
    }

    const getCachedMeliDefaultUserProfile = () => {

        const localStorageKey = `meliuserid-${defaultMeliUserId}`
        const cachedData = localStorage.getItem(localStorageKey)

        if (!cachedData) return null

        try {
            return JSON.parse(cachedData)
        } catch (e) {
            console.warn(`Error parsing cached profile for ${defaultMeliUserId}:`, e)
            localStorage.removeItem(localStorageKey)
            return null
        }
    }

    const getMeliUserDefaultNick = () => {
        const cachedProfile = getCachedMeliDefaultUserProfile()

        if (cachedProfile && cachedProfile.profile && cachedProfile.profile.nickname) {
            return cachedProfile.profile.nickname
        }

        console.warn('❌ No se encontró el nickname del usuario por defecto en caché.')
        return null
    }

    const getMeliUsersWithNickname = () => {
        return meliUsers.map(user => {
            const cachedProfile = getCachedMeliUserProfile(user.meliuserid)
            const nickname = cachedProfile?.profile?.nickname || null
            return {
                ...user,
                nickname
            }
        })
    }

    // ** Initialize on mount
    useEffect(() => {
        if (userData?.id && !isInitialized) {
            const init = async () => {
                const users = await fetchMeliAuthUsers()

                if (users && users.length > 0) {
                    const defaultUser = getMeliDefaultUser()
                    if (defaultUser) {
                        await getMeliUserProfile(defaultUser.meliuserid)
                    }
                }

                setIsInitialized(true)
            }

            init()
        }

        return () => {
            if (intervalRef.current) {
                clearInterval(intervalRef.current)
                intervalRef.current = null
            }
        }
    }, [userData?.id, isInitialized])

    // ** Clear on logout
    useEffect(() => {
        if (!userData?.id) {
            dispatch(clearMeliData())
            setIsInitialized(false)

            if (intervalRef.current) {
                clearInterval(intervalRef.current)
                intervalRef.current = null
            }
        }
    }, [userData?.id, dispatch])

    const value = useMemo(() => ({
        meliUsers,
        getMeliUsersWithNickname,
        defaultMeliUserId,
        isLoading,
        error,
        isInitialized,

        fetchMeliAuthUsers,
        getMeliDefaultUser,
        getMeliAccessToken,
        getDefaultMeliAccessToken,
        isMeliTokenExpired,
        MelirefreshIfNeeded,
        getMeliUserProfile,
        fetchAndCacheAllMeliProfiles,
        getCachedMeliUserProfile,
        getCachedMeliDefaultUserProfile,
        getMeliUserDefaultNick

    }), [
        meliUsers,
        defaultMeliUserId,
        isLoading,
        error,
        isInitialized
    ])

    return (
        <MeliContext.Provider value={value}>
            {children}
        </MeliContext.Provider>
    )
}
